import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Trash, ShieldWarning, Clock, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import { submitAccountDeletion } from "@/lib/api";
import useSeo from "@/lib/useSeo";

const INIT = { name: "", email: "", reason: "" };

export default function DeleteAccount() {
  useSeo({
    title: "Delete My Account",
    description:
      "Request deletion of your IslandHop account and associated data. We process every request within 7 business days.",
  });

  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitAccountDeletion(form);
      setSubmitted({ id: res.id, message: res.message, email: form.email });
      setForm(INIT);
    } catch {
      toast.error("Couldn't submit your request. Please try again or email support@islandhoptt.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="delete-account-page">
      <section className="relative pt-20 lg:pt-28 pb-12 border-b border-ih-line overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-30" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[500px] ih-radial-sunset" />

        <div className="relative max-w-3xl mx-auto px-5 lg:px-10 space-y-6">
          <SectionEyebrow label="Account Deletion" color="orange" />
          <h1 className="font-display font-black text-5xl lg:text-6xl text-white leading-[0.95] text-balance">
            Delete my <br />
            <span className="text-ih-sunset">IslandHop account.</span>
          </h1>
          <p className="text-white/70 text-lg leading-relaxed">
            You can request deletion of your IslandHop account and all associated personal data at any time.
            Submit the form below and our team will permanently remove your record from our systems within
            7 business days.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-8 items-start">
          <aside className="lg:col-span-5 space-y-3" data-testid="deletion-info">
            <InfoCard icon={Trash} title="What gets deleted">
              Your profile, contact details, application history, waitlist record, saved preferences and any
              uploaded content tied to your IslandHop account.
            </InfoCard>
            <InfoCard icon={ShieldWarning} title="What we may retain">
              Anonymized transactional records required by Caribbean law (e.g. tax, fraud prevention,
              dispute records). These cannot identify you personally.
            </InfoCard>
            <InfoCard icon={Clock} title="Processing time">
              Up to <span className="text-white">7 business days</span>. You&apos;ll receive an email
              confirmation once your data has been removed.
            </InfoCard>
            <InfoCard icon={EnvelopeSimple} title="Need help?">
              Email <a className="text-ih-teal underline underline-offset-4" href="mailto:support@islandhoptt.com">support@islandhoptt.com</a>
              {" "}and we&apos;ll walk you through it.
            </InfoCard>
          </aside>

          <div className="lg:col-span-7">
            {submitted ? (
              <SuccessCard data={submitted} onReset={() => setSubmitted(null)} />
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                onSubmit={onSubmit}
                data-testid="account-deletion-form"
                className="bg-ih-surface border border-ih-line rounded-3xl p-8 lg:p-10 space-y-5"
              >
                <div>
                  <h2 className="font-display font-extrabold text-2xl text-white">Submit a deletion request</h2>
                  <p className="text-sm text-white/55 mt-1">All fields marked with * are required.</p>
                </div>

                <Field label="Your name *">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="deletion-name"
                    className={inputCls}
                    autoComplete="name"
                  />
                </Field>

                <Field label="Email associated with your account *">
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="deletion-email"
                    className={inputCls}
                    autoComplete="email"
                    placeholder="you@email.com"
                  />
                </Field>

                <Field label="Reason for deletion (optional)">
                  <textarea
                    rows={4}
                    value={form.reason}
                    onChange={(e) => update("reason", e.target.value)}
                    data-testid="deletion-reason"
                    className={`${inputCls} resize-none`}
                    placeholder="Optional — tell us why you're leaving so we can improve."
                  />
                </Field>

                <p className="text-xs text-white/45 leading-relaxed">
                  By submitting, you confirm you are the owner of the account associated with this email
                  address. We may contact you to verify your identity before processing the deletion.
                </p>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="deletion-submit"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-ih-sunset text-black font-semibold hover:scale-[1.02] disabled:opacity-60 transition w-full sm:w-auto"
                >
                  {loading ? "Submitting..." : "Submit deletion request"}
                </button>
              </motion.form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-black/40 border border-ih-line2 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-ih-sunset transition text-sm";

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs uppercase tracking-[0.16em] text-white/50">{label}</span>
    {children}
  </label>
);

const InfoCard = ({ icon: Icon, title, children }) => (
  <div className="bg-ih-surface border border-ih-line rounded-2xl p-5">
    <Icon weight="duotone" size={22} className="text-ih-sunset mb-3" />
    <p className="font-semibold text-white text-sm mb-1">{title}</p>
    <p className="text-xs text-white/60 leading-relaxed">{children}</p>
  </div>
);

const SuccessCard = ({ data, onReset }) => (
  <div
    data-testid="deletion-success"
    className="bg-ih-surface border border-ih-sunset/40 rounded-3xl p-10 lg:p-14 ih-shadow-sunset text-center"
  >
    <CheckCircle weight="duotone" size={56} className="mx-auto text-ih-sunset mb-5" />
    <h3 className="font-display font-extrabold text-3xl text-white mb-3">Request received</h3>
    <p className="text-white/70 max-w-md mx-auto leading-relaxed">
      Your request has been received. We will process your account deletion within{" "}
      <span className="text-white font-semibold">7 business days</span> and email you a confirmation at{" "}
      <span className="text-white">{data.email}</span>.
    </p>
    <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-white/40">
      Request ID · {data.id}
    </p>
    <button onClick={onReset} className="mt-6 text-sm text-ih-sunset underline underline-offset-4">
      Submit another request
    </button>
  </div>
);
